import React, { useContext, useEffect, useState } from 'react';
import { Modal, Select, InputNumber, Button, message, Switch, Input, Tag, Tooltip, Divider, Badge, Popconfirm } from 'antd';
import { DeleteOutlined, PlusOutlined, ArrowUpOutlined, ArrowDownOutlined, LockOutlined, ThunderboltOutlined, CopyOutlined } from '@ant-design/icons';
import { GetAulaRaConfig } from "../../../dao/GetAulaRaConfig";
import { SaveAulaRaConfig } from "../../../dao/SaveAulaRaConfig";
import { GetAules } from "../../../dao/GetAules";
import { AuthContext } from "../../../config/AuthContext";

const { Option } = Select;

interface ConfiguracioModalProps {
    visible: boolean;
    onCancel: () => void;
    aulaId: string;
    ras: string[];
    availableAulas?: { id: number; nom: string }[];
}

const FIXED_INSTRUMENTS = ['Actividades', 'Practicas', 'Evaluaciones', 'Prueba Practica'];
const RECOVERY_TYPES = [
    { label: 'Máximo', value: 'max', color: '#1890ff' },
    { label: 'Media', value: 'average', color: '#722ed1' },
    { label: 'Reemplaza', value: 'replace', color: '#fa8c16' },
    { label: 'Nota final', value: 'final_grade', color: '#52c41a' }
];

const recoveryColor = (type: string) => RECOVERY_TYPES.find(t => t.value === type)?.color || '#1890ff';
const recoveryLabel = (type: string) => RECOVERY_TYPES.find(t => t.value === type)?.label || type;

const ConfiguracioModal: React.FC<ConfiguracioModalProps> = ({ visible, onCancel, aulaId, ras, availableAulas = [] }) => {
    const { userId } = useContext(AuthContext);
    const [config, setConfig] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [selectedSourceAula, setSelectedSourceAula] = useState<number | null>(null);
    const [aulasList, setAulasList] = useState<{ id: number; nom: string }[]>(availableAulas);

    useEffect(() => {
        if (availableAulas && availableAulas.length > 0) {
            setAulasList(availableAulas);
        }
    }, [availableAulas]);

    useEffect(() => {
        if (visible) {
            if (aulaId) loadConfig();
            // Fetch all user classrooms if availableAulas prop is empty or has only current aula
            if (userId && (!availableAulas || availableAulas.length <= 1)) {
                GetAules(userId).then(data => {
                    if (data && Array.isArray(data) && data.length > 0) {
                        const formatted = data.map((a: any) => ({
                            id: a.id_aula || a.id,
                            nom: a.nom_aula || a.aula || a.nom || `Aula ${a.id_aula || a.id}`
                        }));
                        setAulasList(formatted);
                    }
                }).catch(err => console.error("Error fetching aulas in modal:", err));
            }
        }
    }, [visible, aulaId]);

    const processLoadedConfig = (data: any[], targetRas: string[]) => {
        let loadedConfig = (data || []).map((c: any) => ({ ...c }));

        targetRas.forEach(ra => {
            FIXED_INSTRUMENTS.forEach(inst => {
                const exists = loadedConfig.some((c: any) => c.nom_ra === ra && c.instrument === inst);
                if (!exists) {
                    loadedConfig.push({ nom_ra: ra, instrument: inst, pes: 0, minim: 0, isFixed: true });
                } else {
                    loadedConfig = loadedConfig.map((c: any) =>
                        (c.nom_ra === ra && c.instrument === inst) ? { ...c, isFixed: true } : c
                    );
                }
            });
        });

        loadedConfig = loadedConfig.map((c: any) => ({
            ...c,
            isRecovery: c.isRecovery || false,
            recoveryType: c.recoveryType || 'max',
            targetInstrument: c.targetInstrument || 'Prueba Practica',
            notaMaxima: c.notaMaxima ?? null
        }));

        loadedConfig.sort((a: any, b: any) => {
            if (a.nom_ra !== b.nom_ra) return a.nom_ra.localeCompare(b.nom_ra);
            const aFixed = FIXED_INSTRUMENTS.indexOf(a.instrument);
            const bFixed = FIXED_INSTRUMENTS.indexOf(b.instrument);
            if (aFixed !== -1 && bFixed !== -1) return aFixed - bFixed;
            if (aFixed !== -1) return -1;
            if (bFixed !== -1) return 1;
            return (a.ordre || 0) - (b.ordre || 0);
        });

        return loadedConfig;
    };

    const loadConfig = async () => {
        setLoading(true);
        try {
            const data = await GetAulaRaConfig(aulaId);
            setConfig(processLoadedConfig(data, ras));
        } catch (e) {
            console.error(e);
            message.error("Error al cargar la configuración");
        }
        setLoading(false);
    };

    const handleCopyFromAula = async () => {
        if (!selectedSourceAula) {
            message.warning("Selecciona un aula de origen para copiar");
            return;
        }
        setLoading(true);
        try {
            const sourceData = await GetAulaRaConfig(selectedSourceAula + "");
            if (!sourceData || sourceData.length === 0) {
                message.warning("El aula seleccionada no tiene configuración de evaluación para copiar");
                setLoading(false);
                return;
            }

            // Adapt copied config to the current aula's RAs
            const copiedConfig = processLoadedConfig(sourceData, ras);
            setConfig(copiedConfig);
            message.success("Configuración copiada correctamente. Recuerda pulsar 'Guardar configuración' para guardarla en esta aula.");
        } catch (e) {
            console.error(e);
            message.error("Error al copiar la configuración de la otra aula");
        }
        setLoading(false);
    };

    const handleSave = async () => {
        const weightsByRa: Record<string, number> = {};
        config.forEach(item => {
            if (!item.isRecovery) {
                weightsByRa[item.nom_ra] = (weightsByRa[item.nom_ra] || 0) + (item.pes || 0);
            }
        });
        for (const ra of ras) {
            if (weightsByRa[ra] !== undefined && weightsByRa[ra] !== 100) {
                message.warning(`El peso total para RA${ra} es ${weightsByRa[ra]}% en lugar de 100%`);
            }
        }

        setLoading(true);
        try {
            const configWithOrder: any = [];
            const allRas = Array.from(new Set(config.map(c => c.nom_ra)));
            for (const ra of allRas) {
                const raItems = config.filter(c => c.nom_ra === ra);
                raItems.forEach((item, index) => {
                    configWithOrder.push({ ...item, ordre: index });
                });
            }
            await SaveAulaRaConfig(aulaId, configWithOrder);
            message.success("Configuración guardada");
            onCancel();
        } catch (e) {
            console.error(e);
            message.error("Error al guardar");
        }
        setLoading(false);
    };

    const addInstrumentToRa = (raName: string) => {
        const raIndices = config.map((c, i) => c.nom_ra === raName ? i : -1).filter(i => i !== -1);
        const lastIndex = raIndices.length > 0 ? Math.max(...raIndices) : config.length - 1;
        const newConfig = [...config];
        const newItem = { nom_ra: raName, instrument: 'Nuevo Instrumento', pes: 0, minim: 0, isFixed: false, isRecovery: false, recoveryType: 'max', targetInstrument: 'Prueba Practica', notaMaxima: null };
        if (lastIndex >= 0 && lastIndex < newConfig.length) {
            newConfig.splice(lastIndex + 1, 0, newItem);
        } else {
            newConfig.push(newItem);
        }
        setConfig(newConfig);
    };

    const removeInstrument = (raName: string, indexInRa: number) => {
        const items = config.filter(c => c.nom_ra === raName);
        const itemToRemove = items[indexInRa];
        if (itemToRemove) setConfig(config.filter(c => c !== itemToRemove));
    };

    const updateItem = (raName: string, indexInRa: number, field: string, value: any) => {
        const items = config.filter(c => c.nom_ra === raName);
        const itemToUpdate = items[indexInRa];
        if (!itemToUpdate) return;

        let newConfig = config.map(c => {
            if (c !== itemToUpdate) return c;
            const updated = { ...c, [field]: value };
            if (field === 'recoveryType') {
                if (value === 'final_grade') { updated.pes = 100; }
                else {
                    const target = config.find(k => k.nom_ra === raName && k.instrument === (updated.targetInstrument || 'Prueba Practica'));
                    if (target) updated.pes = target.pes;
                }
            }
            if (field === 'isRecovery' && value === true) {
                if (updated.recoveryType === 'final_grade') { updated.pes = 100; }
                else {
                    const target = config.find(k => k.nom_ra === raName && k.instrument === (updated.targetInstrument || 'Prueba Practica'));
                    if (target) updated.pes = target.pes;
                }
            }
            if (field === 'targetInstrument' && updated.isRecovery && updated.recoveryType !== 'final_grade') {
                const target = config.find(k => k.nom_ra === raName && k.instrument === value);
                if (target) updated.pes = target.pes;
            }
            return updated;
        });

        if (field === 'pes') {
            const name = itemToUpdate.instrument;
            newConfig = newConfig.map(c => {
                if (c.nom_ra === raName && c.isRecovery && c.recoveryType !== 'final_grade' &&
                    (c.targetInstrument === name || (!c.targetInstrument && name === 'Prueba Practica'))) {
                    return { ...c, pes: value };
                }
                return c;
            });
        }

        setConfig(newConfig);
    };

    const moveItem = (raName: string, relativeIndex: number, direction: number) => {
        const raItems = config.filter(c => c.nom_ra === raName);
        const targetIndex = relativeIndex + direction;
        if (targetIndex < 0 || targetIndex >= raItems.length) return;
        const itemA = raItems[relativeIndex];
        const itemB = raItems[targetIndex];
        if (itemB.isFixed) return;
        const newConfig = [...config];
        const indexA = newConfig.indexOf(itemA);
        const indexB = newConfig.indexOf(itemB);
        newConfig[indexA] = itemB;
        newConfig[indexB] = itemA;
        setConfig(newConfig);
    };

    const renderInstrumentCard = (item: any, index: number, raName: string, raItems: any[]) => {
        const isFirst = index === 0;
        const isLast = index === raItems.length - 1;
        const targetOptions = config.filter(c => c.nom_ra === raName && !c.isRecovery);

        const cardStyle: React.CSSProperties = {
            border: item.isRecovery
                ? `2px solid ${recoveryColor(item.recoveryType)}33`
                : item.isFixed ? '1px solid #d9d9d9' : '1px solid #e6f4ff',
            borderLeft: item.isRecovery ? `4px solid ${recoveryColor(item.recoveryType)}` : item.isFixed ? '4px solid #bfbfbf' : '4px solid #1890ff',
            borderRadius: 8,
            padding: '10px 14px',
            marginBottom: 8,
            background: item.isRecovery ? `${recoveryColor(item.recoveryType)}08` : item.isFixed ? '#fafafa' : '#f0f8ff',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            flexWrap: 'wrap' as const,
        };

        return (
            <div key={`${raName}-${index}-${item.instrument}`} style={cardStyle}>
                {/* Icon indicator */}
                <Tooltip title={item.isFixed ? 'Instrumento fijo' : item.isRecovery ? `Recuperación: ${recoveryLabel(item.recoveryType)}` : 'Instrumento base'}>
                    <span style={{ fontSize: 18, minWidth: 22, textAlign: 'center' }}>
                        {item.isFixed ? '📌' : item.isRecovery ? '🔄' : '📊'}
                    </span>
                </Tooltip>

                {/* Instrument name */}
                <div style={{ flex: '0 0 180px' }}>
                    {item.isFixed
                        ? <span style={{ fontWeight: 700, color: '#595959', fontSize: 13 }}>{item.instrument}</span>
                        : <Input
                            size="small"
                            value={item.instrument}
                            onChange={e => updateItem(raName, index, 'instrument', e.target.value)}
                            style={{ fontWeight: 600 }}
                          />
                    }
                </div>

                {/* Peso */}
                <Tooltip title="Peso (%)">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ fontSize: 12, fontWeight: 500, color: '#595959' }}>Peso</span>
                        <InputNumber
                            size="small"
                            min={0} max={100}
                            value={item.pes}
                            onChange={v => updateItem(raName, index, 'pes', v)}
                            disabled={item.isRecovery}
                            style={{ width: 85 }}
                            addonAfter="%"
                        />
                    </div>
                </Tooltip>

                {/* Mínimo */}
                <Tooltip title="Nota mínima requerida (0 = sin mínimo)">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ fontSize: 12, fontWeight: 500, color: '#595959' }}>Mín.</span>
                        <InputNumber
                            size="small"
                            min={0} max={10} step={0.5}
                            value={item.minim}
                            onChange={v => updateItem(raName, index, 'minim', v)}
                            style={{ width: 75 }}
                        />
                    </div>
                </Tooltip>

                {/* Recovery toggle (only non-fixed) */}
                {!item.isFixed && (
                    <Tooltip title="¿Es un instrumento de recuperación?">
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <span style={{ fontSize: 11, color: '#8c8c8c' }}>Recup.</span>
                            <Switch
                                size="small"
                                checked={item.isRecovery}
                                onChange={v => updateItem(raName, index, 'isRecovery', v)}
                                checkedChildren="Sí"
                                unCheckedChildren="No"
                            />
                        </div>
                    </Tooltip>
                )}

                {/* Recovery type (only when isRecovery) */}
                {item.isRecovery && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <span style={{ fontSize: 11, color: '#8c8c8c' }}>Tipo</span>
                        <Select
                            size="small"
                            value={item.recoveryType}
                            style={{ width: 120 }}
                            onChange={v => updateItem(raName, index, 'recoveryType', v)}
                        >
                            {RECOVERY_TYPES.map(t => (
                                <Option key={t.value} value={t.value}>
                                    <span style={{ color: t.color, fontWeight: 600 }}>{t.label}</span>
                                </Option>
                            ))}
                        </Select>
                    </div>
                )}

                {/* Target instrument (only for non-final_grade recoveries) */}
                {item.isRecovery && item.recoveryType !== 'final_grade' && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <span style={{ fontSize: 11, color: '#8c8c8c' }}>→</span>
                        <Select
                            size="small"
                            value={item.targetInstrument}
                            style={{ width: 140 }}
                            onChange={v => updateItem(raName, index, 'targetInstrument', v)}
                        >
                            {targetOptions.map(t => (
                                <Option key={t.instrument} value={t.instrument}>{t.instrument}</Option>
                            ))}
                        </Select>
                    </div>
                )}

                {/* Nota Máxima (only for final_grade) */}
                {item.isRecovery && item.recoveryType === 'final_grade' && (
                    <Tooltip title="Nota máxima que puede obtenerse con este instrumento (vacío = sin límite)">
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <span style={{ fontSize: 11, color: '#8c8c8c' }}>Máx.</span>
                            <InputNumber
                                size="small"
                                min={0} max={10} step={0.5}
                                value={item.notaMaxima}
                                placeholder="∞"
                                onChange={v => updateItem(raName, index, 'notaMaxima', v)}
                                style={{ width: 75 }}
                            />
                        </div>
                    </Tooltip>
                )}

                {/* Spacer */}
                <div style={{ flex: 1 }} />

                {/* Move buttons (non-fixed only) */}
                {!item.isFixed && (
                    <div style={{ display: 'flex', gap: 2 }}>
                        <Tooltip title="Subir">
                            <Button size="small" icon={<ArrowUpOutlined />} disabled={isFirst} onClick={() => moveItem(raName, index, -1)} />
                        </Tooltip>
                        <Tooltip title="Bajar">
                            <Button size="small" icon={<ArrowDownOutlined />} disabled={isLast} onClick={() => moveItem(raName, index, 1)} />
                        </Tooltip>
                    </div>
                )}

                {/* Delete (non-fixed only) */}
                {!item.isFixed && (
                    <Tooltip title="Eliminar instrumento">
                        <Button type="text" danger size="small" icon={<DeleteOutlined />} onClick={() => removeInstrument(raName, index)} />
                    </Tooltip>
                )}
            </div>
        );
    };

    return (
        <Modal
            title={
                <span style={{ fontSize: 16, fontWeight: 700 }}>
                    ⚙️ Configuración de Evaluación por RA
                </span>
            }
            open={visible}
            onCancel={onCancel}
            onOk={handleSave}
            confirmLoading={loading}
            okText="Guardar configuración"
            cancelText="Cancelar"
            width={980}
            styles={{ body: { maxHeight: '75vh', overflowY: 'auto', padding: '16px 24px' } }}
        >
            {/* Copy from another Aula bar */}
            {aulasList && aulasList.filter(a => String(a.id) !== String(aulaId)).length > 0 && (
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    background: '#e6f7ff',
                    border: '1px solid #91d5ff',
                    borderRadius: 8,
                    padding: '10px 16px',
                    marginBottom: 16,
                    gap: 12,
                    flexWrap: 'wrap'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: '1 1 300px' }}>
                        <CopyOutlined style={{ fontSize: 16, color: '#1890ff' }} />
                        <span style={{ fontSize: 13, fontWeight: 600, color: '#0050b3' }}>
                            Copiar configuración de otra aula:
                        </span>
                        <Select
                            placeholder="Selecciona aula origen..."
                            style={{ minWidth: 220, flex: 1 }}
                            value={selectedSourceAula}
                            onChange={setSelectedSourceAula}
                            allowClear
                        >
                            {aulasList
                                .filter(a => String(a.id) !== String(aulaId))
                                .map(a => (
                                    <Option key={a.id} value={a.id}>
                                        {a.nom}
                                    </Option>
                                ))}
                        </Select>
                    </div>
                    <Popconfirm
                        title="¿Copiar configuración?"
                        description="Esto reemplazará los instrumentos y ponderaciones actuales en este modal por los del aula seleccionada. ¿Deseas continuar?"
                        okText="Sí, copiar"
                        cancelText="Cancelar"
                        disabled={!selectedSourceAula}
                        onConfirm={handleCopyFromAula}
                    >
                        <Button
                            type="primary"
                            icon={<CopyOutlined />}
                            disabled={!selectedSourceAula}
                            loading={loading}
                        >
                            Copiar valores
                        </Button>
                    </Popconfirm>
                </div>
            )}

            {/* Legend */}
            <div style={{ display: 'flex', gap: 16, marginBottom: 16, flexWrap: 'wrap', padding: '8px 12px', background: '#fafafa', borderRadius: 6, border: '1px solid #f0f0f0' }}>
                <span style={{ fontSize: 12, color: '#8c8c8c', fontWeight: 600 }}>Leyenda:</span>
                <span style={{ fontSize: 12 }}>📌 Fijo (no editable)</span>
                <span style={{ fontSize: 12 }}>📊 Instrumento base</span>
                <span style={{ fontSize: 12 }}>🔄 Recuperación</span>
                <span style={{ fontSize: 12, color: '#52c41a', fontWeight: 600 }}>■ Nota final</span>
                <span style={{ fontSize: 12, color: '#1890ff', fontWeight: 600 }}>■ Máximo</span>
                <span style={{ fontSize: 12, color: '#722ed1', fontWeight: 600 }}>■ Media</span>
                <span style={{ fontSize: 12, color: '#fa8c16', fontWeight: 600 }}>■ Reemplaza</span>
            </div>

            {ras.map((ra, raIdx) => {
                const raConfig = config.filter(c => c.nom_ra === ra);
                const totalWeight = raConfig.reduce((acc, curr) => curr.isRecovery ? acc : acc + (curr.pes || 0), 0);
                const weightOk = totalWeight === 100;

                return (
                    <div key={ra} style={{ marginBottom: 24 }}>
                        {/* RA Header */}
                        <div style={{
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            background: 'linear-gradient(135deg, #1890ff15, #1890ff05)',
                            border: '1px solid #1890ff30',
                            borderRadius: '8px 8px 0 0',
                            padding: '10px 16px',
                            marginBottom: 0
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <span style={{ fontWeight: 800, fontSize: 15, color: '#1890ff' }}>RA-{ra}</span>
                                <Tag color={weightOk ? 'success' : 'error'} style={{ fontWeight: 600, fontSize: 12 }}>
                                    {totalWeight}% / 100%
                                </Tag>
                                {!weightOk && <span style={{ fontSize: 11, color: '#ff4d4f' }}>⚠ Los pesos no suman 100%</span>}
                            </div>
                            <Button
                                type="primary"
                                ghost
                                size="small"
                                icon={<PlusOutlined />}
                                onClick={() => addInstrumentToRa(ra)}
                            >
                                Añadir instrumento
                            </Button>
                        </div>

                        {/* Instrument cards */}
                        <div style={{
                            border: '1px solid #1890ff30',
                            borderTop: 'none',
                            borderRadius: '0 0 8px 8px',
                            padding: '12px 12px 4px',
                            background: '#fff'
                        }}>
                            {/* Base instruments */}
                            {raConfig.filter(i => !i.isRecovery).length > 0 && (
                                <>
                                    <div style={{ fontSize: 11, color: '#8c8c8c', fontWeight: 600, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                        Instrumentos base
                                    </div>
                                    {raConfig
                                        .map((item, i) => ({ item, i }))
                                        .filter(({ item }) => !item.isRecovery)
                                        .map(({ item, i }) => (
                                            renderInstrumentCard(item, i, ra, raConfig)
                                        ))
                                    }
                                </>
                            )}

                            {/* Recovery instruments */}
                            {raConfig.filter(i => i.isRecovery).length > 0 && (
                                <>
                                    <Divider style={{ margin: '8px 0', fontSize: 11, color: '#8c8c8c' }}>
                                        Recuperaciones / Exámenes
                                    </Divider>
                                    {raConfig
                                        .map((item, i) => ({ item, i }))
                                        .filter(({ item }) => item.isRecovery)
                                        .map(({ item, i }) => (
                                            renderInstrumentCard(item, i, ra, raConfig)
                                        ))
                                    }
                                </>
                            )}
                        </div>
                    </div>
                );
            })}
        </Modal>
    );
};

export default ConfiguracioModal;
